/**
 * Survey Frequency Tracker
 *
 * Core logic for determining whether a survey can be shown and recording
 * survey interactions. Implements a 3-tier frequency control system:
 *
 * 1. IPP-wide: Max surveys across all tools within a rolling window
 * 2. Tool-level: Cooldown after any survey in a tool
 * 3. Interaction: Short cooldown after any survey interaction (submit/dismiss)
 *
 * See surveyFrequencyConfig.ts for specific values.
 */

import {
  SURVEY_WINDOW_MS,
  MAX_IPP_SURVEYS_COUNT,
  INTERACTION_COOLDOWN_MS,
} from './surveyFrequencyConfig'
import { readFrequencyData, writeFrequencyData } from './surveyFrequencyStorage'
import type { SurveyFrequencyData, SurveyRecord, CanShowSurveyResult } from './surveyFrequencyTypes'

/**
 * Checks if a timestamp is within the current window (not expired)
 */
const isWithinWindow = (timestamp: number): boolean => Date.now() - timestamp < SURVEY_WINDOW_MS

/**
 * Filters surveys to only those within the 180-day window
 */
const getActiveSurveys = (data: SurveyFrequencyData): SurveyRecord[] =>
  data.surveys.filter(survey => isWithinWindow(survey.lastShown))

/**
 * Gets the most recent survey interaction (if any)
 */
const getMostRecentSurvey = (data: SurveyFrequencyData): SurveyRecord | undefined => {
  if (data.surveys.length === 0) return undefined

  return data.surveys.reduce((latest, current) =>
    current.lastShown > latest.lastShown ? current : latest
  )
}

/**
 * Checks if the user has had a recent interaction (within 24 hours)
 */
const hasRecentInteraction = (data: SurveyFrequencyData): boolean => {
  const mostRecent = getMostRecentSurvey(data)
  if (!mostRecent) return false

  return Date.now() - mostRecent.lastShown < INTERACTION_COOLDOWN_MS
}

/**
 * Checks if a tool is in cooldown (survey shown in last 180 days)
 */
const isToolInCooldown = (data: SurveyFrequencyData, toolName: string): boolean =>
  data.surveys.some(survey => survey.toolName === toolName && isWithinWindow(survey.lastShown))

/**
 * Removes expired survey records (older than 180 days)
 * Keeps the stored data from growing indefinitely
 */
const cleanupExpiredRecords = (data: SurveyFrequencyData): void => {
  data.surveys = getActiveSurveys(data)
}

/**
 * Determines whether a survey can be shown for a given tool and workflow
 *
 * Checks in order:
 * 1. Recent interaction cooldown (24 hours after any submit/dismiss)
 * 2. Tool cooldown (180 days after any survey in this tool)
 * 3. IPP-wide limit (max 10 surveys in 180 days)
 */
export const canShowSurvey = async (
  toolName: string,
  workflowSlug: string,
  options: { alwaysShow?: boolean } = {}
): Promise<CanShowSurveyResult> => {
  // Allow bypassing all checks for testing/development
  if (options.alwaysShow) {
    return { canShow: true }
  }

  const data = await readFrequencyData()

  // Check 1: Recent interaction cooldown (24 hours)
  if (hasRecentInteraction(data)) {
    return { canShow: false, reason: 'recent_interaction' }
  }

  // Check 2: Tool cooldown (180 days)
  if (isToolInCooldown(data, toolName)) {
    return { canShow: false, reason: 'tool_cooldown' }
  }

  // Check 3: IPP-wide limit
  const activeSurveys = getActiveSurveys(data)
  if (activeSurveys.length >= MAX_IPP_SURVEYS_COUNT) {
    return { canShow: false, reason: 'ipp_max_reached' }
  }

  return { canShow: true }
}

/**
 * Records that a survey was shown for a tool/workflow
 * Call this when the survey modal is displayed to the user
 */
export const recordSurveyShown = async (
  toolName: string,
  workflowSlug: string,
  submitted: boolean
): Promise<void> => {
  const data = await readFrequencyData()

  // Clean up expired records first
  cleanupExpiredRecords(data)

  // Add new survey record
  data.surveys.push({
    toolName,
    workflowSlug,
    lastShown: Date.now(),
    submitted,
  })

  await writeFrequencyData(data)
}

/**
 * Records that a survey was submitted
 */
export const recordSurveySubmitted = async (
  toolName: string,
  workflowSlug: string
): Promise<void> => {
  await recordSurveyShown(toolName, workflowSlug, true)
}

/**
 * Records that a survey was dismissed (closed without submitting)
 */
export const recordSurveyDismissed = async (
  toolName: string,
  workflowSlug: string
): Promise<void> => {
  await recordSurveyShown(toolName, workflowSlug, false)
}

/**
 * Gets the current frequency data (for debugging/testing)
 */
export const getFrequencyData = async (): Promise<SurveyFrequencyData> => readFrequencyData()

/**
 * Gets the current IPP-wide survey count within the window (for debugging/testing)
 */
export const getIppCount = async (): Promise<number> => {
  const data = await readFrequencyData()
  return getActiveSurveys(data).length
}
